import React from "react";

import { BrandMark } from "@javascript/components/BrandMark";
import { MarketingFooter } from "@javascript/components/MarketingFooter";

export default function PrivacyShow() {
  return (
    <>
      <section className="px-4 py-12 sm:px-6 sm:py-20">
        <article className="mx-auto w-full max-w-6xl rounded-2xl border bg-background p-6 shadow-sm sm:p-10">
          <BrandMark size="md" className="font-serif font-semibold text-foreground" />

          <header className="mt-10 max-w-2xl">
            <p className="text-sm font-medium text-primary">Privacy Policy</p>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">Your wedding data stays yours.</h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              What Goldypaper knows about you and your guests, who else touches it, and how to get a copy or have it deleted. Written to be read, not to be skipped.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              <time dateTime="2026-08-17">Last updated: 17 August 2026</time>
            </p>
          </header>

          <div className="mt-10 space-y-10 leading-relaxed text-foreground">
            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Who is responsible</h2>
              <p className="mt-3">
                Goldypaper is run by Nadiar Syaripul, in Indonesia. One person, not a company — and that person is the one answerable for your data. Data protection law calls this role the data controller; in practice it means there is someone to hold to account, and it is him.
              </p>
              <p className="mt-3">
                Anything about privacy goes to{" "}
                <a className="font-medium text-primary underline decoration-primary/40 underline-offset-4" href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">What we hold</h2>

              <h3 className="mt-5 font-semibold">What you give us</h3>
              <p className="mt-2">
                The email address and password you sign in with. Everything you write into your invitation — your names, your dates, your venue, your story. And the photographs you upload.
              </p>
              <p className="mt-3">
                If you sign in with Google instead of a password, Google tells us your email address and an ID number that stands for your account. That is all we ask Google for. Not your contacts, not your calendar, nothing else.
              </p>

              <h3 className="mt-6 font-semibold">What your guests give you</h3>
              <p className="mt-2">
                When someone replies to your invitation, we store their name, how many people they are bringing, whether they are coming, and any message they write.
              </p>
              <p className="mt-3">
                They are telling <em>you</em>, not us. You invited them; we are holding their answer on your behalf. So if a guest wants their reply taken down, they should ask you first — you are the one who invited them.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">What we never collect</h2>
              <p className="mt-3">
                No advertising, and no third-party scripts beyond the analytics explained below. We do not record the IP address you sign in from.
              </p>
              <p className="mt-3">
                The only cookies we set outside of analytics are the one that keeps you signed in while you work, and the “remember me” one, if you ask for it.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Analytics, and choosing whether we use it</h2>
              <p className="mt-3">
                We use Google Analytics, loaded through Google Tag Manager, on the marketing site and the signed-in dashboard — the pages you use to manage your account and build your invitation. It tells us which pages get used and where people get stuck, so we can improve the product. It does not tell us who you are.
              </p>
              <p className="mt-3">
                It never runs on the page your guests actually open. Your invitation’s public address and its RSVP form are excluded outright, by how the page is built, not by a setting — a guest replying to you is never measured, no matter what you choose below.
              </p>
              <p className="mt-3">
                Nothing is measured until you say yes. The first time you visit, a banner asks you to accept or decline — declining is exactly as easy as accepting, and nothing runs before you answer either way. If you accept, Google sets its own analytics cookies (used to tell one visit from another); see{" "}
                <a className="font-medium text-primary underline decoration-primary/40 underline-offset-4" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google’s Privacy Policy</a>{" "}
                for how it handles that data. You can change your mind at any time from “Cookie preferences” in the footer of any page.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-semibold">Where your data lives</h2>
              <p className="mt-3 max-w-3xl">
                Goldypaper is run from Indonesia, but the computers holding your data are not. Your data leaves the country in ordinary use — so rather than say it vaguely, here is exactly who holds what, and where.
              </p>
              <div className="mt-5 overflow-x-auto rounded-lg border" tabIndex={0}>
                <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
                  <thead className="bg-muted text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">What it holds or does</th>
                      <th className="px-4 py-3 font-medium">Where</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr><td className="px-4 py-3 font-medium">Advin Servers</td><td className="px-4 py-3">The computer Goldypaper runs on, and the database holding accounts, invitations, and guest replies.</td><td className="px-4 py-3">United States</td></tr>
                    <tr><td className="px-4 py-3 font-medium">Backblaze B2</td><td className="px-4 py-3">The photographs you upload, and the resized copies made from them.</td><td className="px-4 py-3">European Union</td></tr>
                    <tr><td className="px-4 py-3 font-medium">Amazon SES</td><td className="px-4 py-3">The emails we send you — confirming your address, resetting your password, welcoming you.</td><td className="px-4 py-3">United States</td></tr>
                    <tr><td className="px-4 py-3 font-medium">Google</td><td className="px-4 py-3">Sign-in, if you choose it. Google never sees your invitation or your guest list.</td><td className="px-4 py-3">Worldwide; no single country</td></tr>
                    <tr><td className="px-4 py-3 font-medium">Google Analytics</td><td className="px-4 py-3">Usage data from our marketing pages and dashboard — only if you accept analytics cookies. Never runs on your guests’ RSVP page.</td><td className="px-4 py-3">Worldwide; no single country</td></tr>
                    <tr><td className="px-4 py-3 font-medium">Cloudflare</td><td className="px-4 py-3">Points visitors at goldypaper.com and sits in front of it, so it sees the traffic passing through.</td><td className="px-4 py-3">Worldwide; no single country</td></tr>
                    <tr><td className="px-4 py-3 font-medium">The company hosting our support inbox</td><td className="px-4 py-3">Whatever you write to us at our support address, and the spam filtering in front of it.</td><td className="px-4 py-3">Not established</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Getting a copy, or having it deleted</h2>
              <p className="mt-3">
                Email{" "}
                <a className="font-medium text-primary underline decoration-primary/40 underline-offset-4" href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>{" "}
                from the address your account uses, and say what you want — a copy of your data, or all of it gone. We use that address to check the request is really from you. Once it is confirmed, it is done within 30 days.
              </p>
              <p className="mt-3">
                There is no delete button inside Goldypaper yet, and we would rather tell you that than imply otherwise. There is also no archive and no grace period: once your data is deleted, it is not sitting somewhere waiting to be restored.
              </p>
            </section>
          </div>
        </article>
      </section>

      <MarketingFooter illustration={false} />
    </>
  );
}
